import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
  type AxiosError,
} from 'axios'
import { showFailToast, showLoadingToast } from 'vant'
import 'vant/es/toast/style'  // 手动加载 Toast CSS：showFailToast/showLoadingToast 是 JS 调用，unplugin 不会自动引入样式
import {TOKEN_KEY, USER_KEY, WX_AUTH_STATE} from "@/utils/constants.ts";

// ============================================================
// 类型定义
// ============================================================

/** 后端统一响应结构 */
export interface ApiResponse<T = any> {
  code: number
  data: T
  msg: string
}

/** 分页数据结构 */
export interface PaginatedData<T> {
  list: T[]
  total: number
  page: number
  pageSize: number
}

/** 通用请求配置 */
export interface RequestConfig {
  url: string
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  params?: Record<string, any>
  data?: Record<string, any>
  /** 是否显示 loading 遮罩，默认 true */
  loading?: boolean
  /** loading 提示文案 */
  loadingText?: string
  /** 是否在请求失败时自动弹出错误 toast，默认 true */
  showError?: boolean
}

// ============================================================
// 自定义错误类型
// ============================================================

/** 业务错误（后端返回非 0/200 code） */
export class BusinessError extends Error {
  code: number
  constructor(code: number, message: string) {
    super(message)
    this.name = 'BusinessError'
    this.code = code
  }
}

/** 登录态过期 */
export class AuthError extends Error {
  constructor(message = '登录已过期，请重新登录') {
    super(message)
    this.name = 'AuthError'
  }
}

/** 网络/服务器错误 */
export class NetworkError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'NetworkError'
  }
}

// ============================================================
// Axios 实例
// ============================================================

const http: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 15000,
})

// ---- 请求拦截器：自动携带 Token ----
http.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(TOKEN_KEY)
    if (token ) {
      config.headers['token-MAY_PARENT'] = token
    }
    return config
  },
  (error) => Promise.reject(error),
)

// ---- 响应拦截器：统一错误处理 ----
http.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    const { code, msg } = response.data

    // 成功码
    if (code === 0 || code === 200) {
      return response
    }

    // 登录态过期
    if (code === 401) {
      localStorage.removeItem(USER_KEY)
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(WX_AUTH_STATE)
      window.location.reload()
      return Promise.reject(new AuthError(msg))
    }

    // 其他业务错误
    return Promise.reject(new BusinessError(code, msg || '请求失败'))
  },
  (error: AxiosError) => {
    let message: string

    if (error.code === 'ECONNABORTED') {
      message = '请求超时，请检查网络'
    } else if (error.response) {
      const status = error.response.status
      switch (status) {
        case 500:
          message = '服务器异常，请稍后重试'
          break
        case 404:
          message = '请求的资源不存在'
          break
        case 403:
          message = '没有访问权限'
          break
        default:
          message = `请求错误 (${status})`
      }
    } else if (error.message?.includes('Network Error')) {
      message = '网络连接失败，请检查网络'
    } else {
      message = '网络异常，请稍后重试'
    }

    return Promise.reject(new NetworkError(message))
  },
)

// ============================================================
// 对外暴露的请求方法
// ============================================================

/**
 * 通用请求
 *
 * @example
 * // 返回的 data 直接是后端 ApiResponse.data 的内容，已自动解包
 * const userInfo = await request<UserInfo>({ url: '/user/info' })
 */
export async function request<T = any>(config: RequestConfig): Promise<T> {
  const {
    loading = true,
    loadingText = '加载中...',
    showError = true, // 记得把解构里的 showError 加回来，控制是否弹窗
    url,
    method = 'GET',
    params,
    data,
  } = config

  let loadingInstance: any = null
  if (loading) {
    loadingInstance = showLoadingToast({
      message: loadingText,
      forbidClick: true,
      duration: 0, // 设为 0 避免与 failToast 竞态
    })
  }

  try {
    const response = await http<ApiResponse<T>>({ url, method, params, data })
    return response.data.data
  } catch (error) {
    // 先关 loading toast，再弹 fail toast，避免 Vant 内部两个 toast 冲突
    if (loadingInstance) {
      loadingInstance.close()
      loadingInstance = null
    }
    if (showError && (error instanceof BusinessError || error instanceof NetworkError)) {
      showFailToast({
        message: error.message,
        duration: 2000,
      })
    }
    throw error
  } finally {
    // 兜底关闭 loading（正常流程或 catch 未覆盖的场景）
    if (loadingInstance) {
      loadingInstance.close()
    }
  }
}

/**
 * GET 请求
 */
export function get<T = any>(
  url: string,
  params?: Record<string, any>,
  config?: Omit<RequestConfig, 'url' | 'method' | 'params'>,
): Promise<T> {
  return request<T>({ ...config, url, method: 'GET', params })
}

/**
 * POST 请求
 */
export function post<T = any>(
  url: string,
  data?: Record<string, any>,
  config?: Omit<RequestConfig, 'url' | 'method' | 'data'>,
): Promise<T> {
  return request<T>({ ...config, url, method: 'POST', data })
}

/**
 * PUT 请求
 */
export function put<T = any>(
  url: string,
  data?: Record<string, any>,
  config?: Omit<RequestConfig, 'url' | 'method' | 'data'>,
): Promise<T> {
  return request<T>({ ...config, url, method: 'PUT', data })
}

/**
 * DELETE 请求
 */
export function del<T = any>(
  url: string,
  params?: Record<string, any>,
  config?: Omit<RequestConfig, 'url' | 'method' | 'params'>,
): Promise<T> {
  return request<T>({ ...config, url, method: 'DELETE', params })
}

/** 原始 axios 实例（供特殊场景直接使用） */
export { http }
